import { useEffect, useState } from 'react'
import axios from '@fs/zion-axios'
import { appConfig } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'
import { allLocales } from '@fs/zion-locale'

const debug = zionDebug('zion-cms')

const hasProcess = typeof process !== 'undefined'

const { BRIGHTSPOT_API_KEY, BRIGHTSPOT_URL, BRIGHTSPOT_RESOLVE_CLIENT_ID } = appConfig
const ENV_BRIGHTSPOT_API_KEY = hasProcess && process.env.FS_CLIENT_APP_BRIGHTSPOT_API_KEY
const ENV_BRIGHTSPOT_URL = hasProcess && process.env.FS_CLIENT_APP_BRIGHTSPOT_URL
const ENV_BRIGHTSPOT_RESOLVE_CLIENT_ID = hasProcess && process.env.FS_CLIENT_APP_BRIGHTSPOT_RESOLVE_CLIENT_ID

type SiteInfoParams = {
  url?: string
  previewId?: string
}

type Status = 'loading' | 'done' | 'error'

type SiteInfoPathType = {
  site: string | null
  relativeUrl: string | null
  contentType: string | null
  status: Status
}

/**
 * Calls the Brightspot site info endpoint to resolve the URL (if it is found or not)
 * @param {string} url - the URL to be resolved
 * @param {string} previewId - the preview ID for the specific content
 * @returns {{site: {string | null}, relativeUrl: {string | null}, status: {'loading' | 'done' | 'error'}}}
 */
export default function useSiteInfoPath({ url, previewId }: SiteInfoParams): SiteInfoPathType {
  const [site, setSite] = useState(null)
  const [relativeUrl, setRelativeUrl] = useState(null)
  const [contentType, setContentType] = useState(null)
  const [status, setStatus] = useState<Status>('loading') // loading | done | error

  useEffect(() => {
    let cancelled = false

    if (url || previewId) {
      try {
        callSiteInfo({ url, previewId })
          .then((result) => {
            if (!cancelled && result?.status === 200) {
              setSite(result?.data?.site_name)
              setRelativeUrl(result?.data?.relative_path)
              setContentType(result?.data?.content_type)
              setStatus('done')
            } else {
              setStatus('error')
            }
          })
          .catch((error) => {
            setStatus('error' as Status)
            // If this is something other than a 404 then log it so we can debug it
            if (error?.response?.status !== 404) {
              console.error(`Unable to resolve Brightspot path - unexpected error: ${error}`)
            }
          })
      } catch (e) {
        console.error(`Unable to resolve Brightspot path - unexpected exception: ${e}`)
        setSite(null)
        setRelativeUrl(null)
        setStatus('error')
      }
    }

    return () => {
      cancelled = true
    }
  }, [url, previewId])

  return { site, relativeUrl, contentType, status }
}

async function callSiteInfo({ url, previewId }: SiteInfoParams) {
  debug('callSiteInfo:start', { url, previewId })
  let requestUrl = `${BRIGHTSPOT_URL || ENV_BRIGHTSPOT_URL}siteinfo?`
  if (previewId) {
    requestUrl += `preview_id=${previewId}`
  } else if (url) {
    requestUrl += `full_url=${encodeURIComponent(url)}`
  } else {
    throw new Error("No 'url' or 'previewId' parameter provided")
  }

  const result = await axios.get(requestUrl, {
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
      'x-api-key': BRIGHTSPOT_API_KEY || ENV_BRIGHTSPOT_API_KEY,
      'x-client-id': `${BRIGHTSPOT_RESOLVE_CLIENT_ID || ENV_BRIGHTSPOT_RESOLVE_CLIENT_ID}`,
    },
  })
  debug('callSiteInfo:success', result?.data)
  return result
}

/**
 * Returns the URL for the page. If the url is not provided, it will use the current window location.
 * - Defaults to https://www.familysearch.org if the origin is not available.
 * - If the pathname starts with a locale, it will be removed.
 * - If the pathname does not end with a slash, it will be added.
 * @param {string} url - The URL for the page.
 * @returns {string} The URL for the page.
 */
export function getBrightspotPageUrl(url?: string) {
  let finalUrl = url
  if (!finalUrl) {
    const origin = window?.location?.origin || process.env.BASE_URL
    const pathname = window?.location?.pathname || ''
    // remove the locale from the beginning of the pathname if it exists
    const path = pathname.replace(new RegExp(`^/(${allLocales.join('|')}|zh-Hans|zh-Hant)/`), '/')
    finalUrl = `${origin}${path}`
  }
  // add a trailing slash if it doesn't exist
  if (!finalUrl.endsWith('/')) {
    finalUrl += '/'
  }
  // decode the URL
  return decodeURIComponent(finalUrl)
}
